import React from 'react';
import { Calendar, MapPin, Clock, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { getImageUrl } from '../../utils/imageUtils.js';

const labels = {
  ne: {
    title: 'कार्यक्रमहरू',
    subtitle: 'यस क्षेत्रका आगामी र हालका कार्यक्रमहरू।',
    viewDetails: 'विस्तृत हेर्नुहोस्',
    noEvents: 'यस क्षेत्रमा हाल कुनै कार्यक्रम उपलब्ध छैन।',
  },
  en: {
    title: 'Events',
    subtitle: 'Upcoming and current events for this sector.',
    viewDetails: 'View Details',
    noEvents: 'No events available for this sector.',
  },
};

const getStatusLabel = (status, language) => {
  const labels_map = {
    ne: {
      current: 'चलिरहेको',
      upcoming: 'आगामी',
      past: 'सम्पन्न',
    },
    en: {
      current: 'Current',
      upcoming: 'Upcoming',
      past: 'Past',
    },
  };
  return labels_map[language]?.[status] || labels_map['ne']?.[status] || status;
};

const getStatusBadge = (status) => {
  const configs = {
    current:  { color: '#059669', bg: 'rgba(5, 150, 105, 0.1)', border: 'rgba(5, 150, 105, 0.25)' },
    upcoming: { color: '#2563eb', bg: 'rgba(37, 99, 235, 0.1)',  border: 'rgba(37, 99, 235, 0.25)' },
    past:     { color: '#6b7280', bg: 'rgba(107, 114, 128, 0.1)', border: 'rgba(107, 114, 128, 0.25)' },
  };
  return configs[status] || configs.current;
};

const stripHtml = (html) => {
  if (!html) return '';
  return html.replace(/<[^>]*>?/gm, '').trim();
};

const truncateText = (text, maxLength = 120) => {
  const clean = stripHtml(text);
  if (!clean) return '';
  if (clean.length <= maxLength) return clean;
  return clean.substring(0, maxLength) + '...';
};

const SectorEvents = ({ events, language = 'ne' }) => {
  const t = labels[language] || labels.ne;

  if (!events || events.length === 0) {
    return (
      <section className="sector-events">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">{t.title}</h2>
            <p className="section-subtitle">
              {t.subtitle}
            </p>
          </div>
          <div className="sector-events__empty">
            <div className="sector-events__empty-icon">
              <Calendar size={36} />
            </div>
            <p className="no-events-message">{t.noEvents}</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="sector-events">
      <div className="container">
        <div className="section-header">
          <h2 className="section-title">{t.title}</h2>
          <p className="section-subtitle">
            {t.subtitle}
          </p>
        </div>

        <div className="events__grid">
          {events.map((event, index) => {
            const date = event.date
              ? new Date(event.date).toLocaleDateString(language === 'en' ? 'en-US' : 'ne-NP', {
                  day: 'numeric',
                  month: 'short',
                  year: 'numeric',
                })
              : 'TBA';
            const status = event.status || 'current';
            const statusLabel = getStatusLabel(status, language);
            const badge = getStatusBadge(status);

            return (
              <div key={event._id || index} className="event-card">
                <div className="event-card__header">
                  {event.imageUrl ? (
                    <img
                      src={getImageUrl(event.imageUrl)}
                      alt={event.title}
                      className="event-card__image"
                    />
                  ) : (
                    <div className="event-card__image-placeholder">
                      <Calendar size={44} />
                    </div>
                  )}
                  <div
                    className="event-card__status"
                    style={{
                      color: badge.color,
                      background: badge.bg,
                      border: `1px solid ${badge.border}`,
                    }}
                  >
                    <span>● {statusLabel}</span>
                  </div>
                </div>

                <div className="event-card__content">
                  <div className="event-card__meta">
                    {event.date && (
                      <div className="event-card__meta-item">
                        <Calendar size={13} />
                        <span>{date}</span>
                      </div>
                    )}
                    {event.time && (
                      <div className="event-card__meta-item">
                        <Clock size={13} />
                        <span>{event.time}</span>
                      </div>
                    )}
                    {event.location && (
                      <div className="event-card__meta-item">
                        <MapPin size={13} />
                        <span>{event.location}</span>
                      </div>
                    )}
                  </div>

                  <h3 className="event-card__title" title={event.title}>
                    {event.title}
                  </h3>

                  {event.description && (
                    <p className="event-card__description">
                      {truncateText(event.description, 110)}
                    </p>
                  )}

                  <div className="event-card__footer">
                    <Link to={`/events/${event._id}`} className="event-card__link">
                      <span>{t.viewDetails}</span>
                      <ArrowRight size={15} />
                    </Link>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default SectorEvents;
